import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { loadInstalledAppAdvisers } from "./installedAppAdvisers";
import { Helmet } from "react-helmet-async";
import { auth } from "../../../backend/firebaseConfig";
import Sidebar from "../../DesignSystem/Sidebar";
import Breadcrumb from "../../DesignSystem/Breadcrumb/Breadcrumb";
import SearchField from "../../DesignSystem/Inputs/SearchField";
import { ReactComponent as SortIcon } from "../../../assets/icons/paraplanningDashboard/Sort.svg";
import Pagination from "../../DesignSystem/Pagination/Pagination";
import "../../../css/App.css";
import "./InstalledAppsPage.css";

const API_URL = (process.env.REACT_APP_SNOWFLAKE_API_URL || "http://localhost:10001").replace(/\/$/, "");
const PAGE_SIZE = 100;
const COLUMNS = [
  ["userName", "User"], ["name", "App"], ["description", "Description"],
  ["publisher", "Publisher"], ["category1", "Category"], ["category2", "Additional category"],
  ["billingType", "Billing type"],
  ["publishedAt", "Published"], ["updatedAt", "Updated"],
];
const date = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleString("en-GB", { timeZone: "Europe/London" });
};

export default function InstalledAppsPage() {
  const [advisersByUserId, setAdvisersByUserId] = useState({});
  const [adviserLinkError, setAdviserLinkError] = useState("");
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [term, setTerm] = useState("");
  const [sort, setSort] = useState({ key: "", direction: 1 });
  const [refresh, setRefresh] = useState(0);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const inFlight = useRef(false);

  useEffect(() => {
    let active = true;
    loadInstalledAppAdvisers().then(matches => {
      if (active) setAdvisersByUserId(matches);
    }).catch(() => {
      if (active) setAdviserLinkError("Adviser profile links could not be loaded.");
    });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => { setPage(1); setTerm(search.trim()); }, 350);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      if (!document.hidden && !inFlight.current) setRefresh(value => value + 1);
    }, 60000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    async function load() {
      inFlight.current = true;
      setLoading(true);
      setError("");
      try {
        const token = await auth.currentUser?.getIdToken();
        if (!token) throw new Error("Please sign in again.");
        const params = new URLSearchParams({ page: String(page), pageSize: String(PAGE_SIZE), search: term });
        const response = await fetch(`${API_URL}/api/installed-apps?${params}`, {
          headers: { Authorization: `Bearer ${token}` }, signal: controller.signal,
        });
        const body = await response.json().catch(() => ({}));
        if (!response.ok) {
          if (active && (response.status === 401 || response.status === 403)) setResult(null);
          throw new Error(body.error || "Unable to load installed apps.");
        }
        if (active) {
          const lastPage = Math.max(1, Math.ceil(body.total / PAGE_SIZE));
          if (page > lastPage) setPage(lastPage);
          else setResult(body);
        }
      } catch (loadError) {
        if (active) {
          if (/Staff access|sign in|token/i.test(loadError.message)) setResult(null);
          setError(loadError.message);
        }
      } finally {
        if (active) { setLoading(false); inFlight.current = false; }
      }
    }
    load();
    return () => { active = false; controller.abort(); };
  }, [page, term, refresh]);

  const totalPages = Math.max(1, Math.ceil((result?.total || 0) / PAGE_SIZE));
  // Previous results are only displayed for the same page and search.
  const current = result?.page === page && result?.search === term;
  const rows = current ? [...result.rows].sort((a, b) => {
    if (!sort.key) return 0;
    const left = a[sort.key];
    const right = b[sort.key];
    if (left == null) return right == null ? 0 : 1;
    if (right == null) return -1;
    const comparison = sort.key === "publishedAt" || sort.key === "updatedAt"
      ? (new Date(left).getTime() || 0) - (new Date(right).getTime() || 0)
      : String(left).localeCompare(String(right), "en-GB", { numeric: true, sensitivity: "base" });
    return comparison * sort.direction;
  }) : [];

  return (
    <div className="Main-Page">
      <Helmet><title>Installed IO Apps</title></Helmet>
      <Sidebar isSetExpanded={true} />
      <main className="Main-Container table-contained-page column gap-xs">
        <Breadcrumb items={[{ label: "Finance", to: "/Department/Finance" }, { label: "Installed IO Apps" }]} />
        <header className="installed-apps-header row gap-l flex-wrap">
          <div className="installed-apps-heading fit-column gap-xs">
            <h3 className="FP_H3">Installed IO Apps</h3>
          </div>
          <div className="installed-apps-controls row gap-s">
          <div className="fci-search-control">
            <SearchField id="installed-apps-search" label="Search installed apps" placeholder="Search apps, publishers or users"
              value={search} onChange={event => setSearch(event.target.value.slice(0, 200))} />
          </div>

          </div>
        </header>
        {adviserLinkError && <p className="FP_Help_Text text-error" role="status">{adviserLinkError}</p>}
        {error && <p className="FP_Help_Text text-error" role="alert">{error}{current ? " Showing the last successful result." : ""}</p>}
        <section className="card-corner-radius" aria-busy={loading}>
          <div className="table-scroll-container">
            <table aria-label="Installed IO Apps">
              <thead><tr><th scope="col" aria-label="Row number" className="FP_Help_Text_Semibold uppercase white-text" />{COLUMNS.map(([key, label]) => <th scope="col" key={key} className="FP_Help_Text_Semibold uppercase white-text" aria-sort={sort.key === key ? (sort.direction === 1 ? "ascending" : "descending") : "none"}>
                <button type="button" className="table-heading-content table-sort-button" aria-label={"Sort by " + label}
                  onClick={() => setSort(previous => ({ key, direction: previous.key === key ? -previous.direction : 1 }))}>
                  {label} <SortIcon aria-hidden="true" />
                </button>
              </th>)}</tr></thead>
              <tbody>
                {rows.map((row, index) => <tr key={row.rowKey ?? `${row.appId}-${row.userId}-${index}`}>
                  <td className="FP_Help_Text text-black">{(page - 1) * PAGE_SIZE + index + 1}</td>
                  {COLUMNS.map(([key]) => {
                    const name = String(row[key] ?? "-");
                    const adviserKey = advisersByUserId[String(row.userId)];
                    return <td key={key} className="FP_Help_Text text-black">
                      {key === "name" || key === "userName" ? (
                        <div className="installed-apps-identity">
                          {key === "userName" && adviserKey
                            ? <Link className="table-url-link text-primary-1" to={"/advisers/" + encodeURIComponent(adviserKey) + "?tab=ioDetails"}>{name}</Link>
                            : <span>{name}</span>}
                          <span className="FP_Help_Text text-grey" aria-label={key === "name" ? "App ID" : "User ID"}>
                            {String(row[key === "name" ? "appId" : "userId"] ?? "-")}
                          </span>
                        </div>
                      ) : key === "publishedAt" || key === "updatedAt" ? date(row[key]) : name}
                    </td>;
                  })}
                </tr>)}
                {!rows.length && <tr><td colSpan={COLUMNS.length + 1} className="FP_Help_Text primary">{loading ? "Loading installed apps…" : error ? "Installed apps are unavailable." : term ? "No installed apps match your search." : "No installed-app records found."}</td></tr>}
              </tbody>
            </table>
          </div>
        </section>
        {current && totalPages > 1 && <Pagination currentPage={page} totalPages={totalPages} onPageChange={value => { if (!loading) setPage(value); }} />}
      </main>
    </div>
  );
}
